import { Button, Card, CardContent, SwipeableDrawer } from '@material-ui/core';
import { useMutation, useQueryClient } from 'react-query';
import { AddCircleOutline } from '@material-ui/icons';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useImmer } from 'use-immer';
import toast from 'react-hot-toast';
import Modal from 'react-modal';
import Toggle from 'react-toggle';

import {
  AddLeadPartnerProps,
  Guid,
  LeadPartnerDto,
  PaginatedNameFilterOptions,
} from '@project/types';
import {
  AddLeadPartnerForm,
  BasicTable,
  PaginationButtons,
  SearchField,
} from '@project/components';
import { useLeadPartnerData, useUpdateLeadPartnerStatus } from '@project/react-queries';
import { decodedToken, leadPartnerApiService } from '@project/services';

const LEAD_PARTNER_UPDATE_ROLES = ['SuperAdministrator'];

const customStyles = () => {
  return {
    overlay: {
      zIndex: 10,
      background:
        'linear-gradient(290deg, #061F40D9 0%, #031020D9 10%, #101925D9 30%, #0C0C0CD9 100%) 0% 0% no-repeat padding-box',
      boxShadow: '0px 20px 50px #3E3E3E4D',
      filter: 'blur(0px)',
    },
    content: {
      top: '50%',
      left: '50%',
      width: '420px',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
      borderRadius: '20px',
      zIndex: 100,
      opacity: 1,
    },
  };
};

Modal.setAppElement('#root');

export const LeadPartnerScreen: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [leadPartnerToDisable, setLeadPartnerToDisable] = useState<LeadPartnerDto | null>(null);
  const canUpdateLeadPartner = LEAD_PARTNER_UPDATE_ROLES.includes(decodedToken.getRole());
  const history = useHistory();
  const queryClient = useQueryClient();
  const _handleSuccess = () => {
    queryClient.invalidateQueries(['lead-partners']);
    setTimeout(function waitForToast() {
      history.push('/admin/lead-partners');
    }, 2500);
  };

  const _addLeadPartner = (formData: AddLeadPartnerProps) => {
    return leadPartnerApiService.addLeadPartner(formData);
  };

  const _deleteData = useMutation(
    'deleteLeadPartner',
    (id: Guid) => leadPartnerApiService.removeLeadPartner(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['lead-partners']);
        toast.success('Successfully removed lead partner');
      },
      onError: () => {
        toast.error('Error removing lead partner');
      },
    },
  );

  const _addLeadPartnerFailed = () => toast.error('Error adding lead partner');

  const [leadPartnerFilterState, setLeadPartnerFilterState] = useImmer<PaginatedNameFilterOptions>({
    pageNum: 1,
    pageEntries: 10,
    name: '',
  });

  const setPage = (pageNumber: number, pageEntries: number) => {
    setLeadPartnerFilterState({
      ...leadPartnerFilterState,
      pageNum: pageNumber,
      pageEntries,
    });
  };

  const leadPartnerQuery = useLeadPartnerData(leadPartnerFilterState);
  const _updateStatus = useUpdateLeadPartnerStatus(leadPartnerFilterState);

  const _setLeadPartnerStatus = (leadPartnerId: Guid, disabled: boolean) =>
    _updateStatus.mutate({ leadPartnerId, disabled });

  const _handleStatusToggle = (leadPartner: LeadPartnerDto) => {
    if (leadPartner.disabled) {
      _setLeadPartnerStatus(leadPartner.id, false);
      return;
    }

    setLeadPartnerToDisable(leadPartner);
  };

  const _confirmDisableLeadPartner = () => {
    _setLeadPartnerStatus(leadPartnerToDisable.id, true);
    setLeadPartnerToDisable(null);
  };

  const _isStatusUpdating = (leadPartnerId: Guid) =>
    _updateStatus.isLoading && _updateStatus.variables?.leadPartnerId === leadPartnerId;

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Utm Source',
        accessor: 'utmSource',
      },
      {
        Header: 'Viable Days Count',
        accessor: 'viableDaysCount',
      },
      {
        Header: 'Enabled',
        accessor: 'disabled',
        Cell: ({ row }) => (
          <Toggle
            icons={false}
            checked={!row?.original?.disabled}
            disabled={!canUpdateLeadPartner || _isStatusUpdating(row?.original?.id)}
            onChange={() => _handleStatusToggle(row?.original)}
          />
        ),
      },
    ],
    [canUpdateLeadPartner, _updateStatus.isLoading, _updateStatus.variables],
  );

  return (
    <>
      <div className="flex flex-row justify-between">
        <div className="mt-2 flex justify-start mx-8 text-xl">Lead Partner</div>
        <Button
          className="mt-2 flex bg-tertiary text-white hover:bg-tertiary-dark mr-8"
          size="small"
          startIcon={<AddCircleOutline />}
          onClick={() => setDrawerOpen(true)}
        >
          Add New
        </Button>
      </div>
      <div className="border-b border-black mt-2 flex justify-start mx-8" />
      <div className="flex justify-start mx-8 mt-2 space-x-2">
        <SearchField
          placeholder="Name"
          value={leadPartnerFilterState.name}
          onChange={(value) => {
            setLeadPartnerFilterState({
              ...leadPartnerFilterState,
              name: value,
              pageNum: 1,
            });
          }}
          isLoading={leadPartnerQuery.isFetching}
        />
      </div>
      {!leadPartnerQuery.isLoading && (
        <>
          <div className="flex flex-col flex-grow mx-8 mt-2">
            <Card className="flex flex-grow shadow-2xl">
              <CardContent className="flex flex-col flex-grow p">
                <BasicTable
                  tableData={leadPartnerQuery.data.items}
                  tableColumns={columns}
                  path=""
                  isViewable={false}
                  isDeletable
                  onDelete={({ id }) => _deleteData.mutateAsync(id)}
                />
              </CardContent>
            </Card>
          </div>
          <PaginationButtons
            page={leadPartnerFilterState.pageNum}
            setPage={setPage}
            pageIndex={leadPartnerQuery.data.pageIndex}
            totalPages={leadPartnerQuery.data.totalPages}
            pageEntries={leadPartnerFilterState.pageEntries}
          />
        </>
      )}
      <SwipeableDrawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onOpen={() => setDrawerOpen(true)}
      >
        <AddLeadPartnerForm
          initialValues={{
            name: '',
            email: '',
            utmSource: '',
          }}
          submitForm={_addLeadPartner}
          onSuccess={() => {
            _handleSuccess();
            setDrawerOpen(false);
          }}
          onFailure={_addLeadPartnerFailed}
        />
      </SwipeableDrawer>
      <Modal
        isOpen={!!leadPartnerToDisable}
        onRequestClose={() => setLeadPartnerToDisable(null)}
        style={customStyles()}
        contentLabel="Disable Lead Partner"
      >
        <div className="flex flex-col">
          <div className="text-lg">Disable {leadPartnerToDisable?.name}?</div>
          <div className="text-sm mt-3">
            Disabling this partner means new sign-ups using their UTM source will no longer be
            linked to them. Existing users are unaffected.
          </div>
          <div className="flex flex-row justify-end mt-6 space-x-2">
            <Button size="small" onClick={() => setLeadPartnerToDisable(null)}>
              Cancel
            </Button>
            <Button
              className="bg-tertiary text-white hover:bg-tertiary-dark"
              size="small"
              onClick={_confirmDisableLeadPartner}
            >
              Disable
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
